import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AreasSection from '../components/AreasSection';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiClock, FiShield, FiCheck, FiPhone, FiCalendar, FiStar, FiUser } = FiIcons;

const DomesticPage = () => {
  const { area } = useParams();
  const currentArea = area ? formatAreaName(area) : 'Aylesbury';

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const services = [
    {
      title: "Regular Home Cleaning",
      description: "dusting, vacuuming, mopping & tidying",
      image: "/img/domestic-homepage.webp"
    },
    {
      title: "Kitchen Cleaning", 
      description: "sparkling sinks, hobs, worktops & appliances",
      image: "/img/domestic-1.webp"
    },
    {
      title: "Bathroom Cleaning",
      description: "fresh and hygienic toilets, showers & tiles", 
      image: "/img/domestic-2.webp"
    },
    {
      title: "Living & Bedrooms",
      description: "dust-free surfaces, polished mirrors & fresh floors",
      image: "/img/bedroom-cleaning.webp"
    },
    {
      title: "Deep Cleaning",
      description: "inside ovens, fridges, cupboards & skirting boards",
      image: "/img/deep-cleaning.webp"
    },
    {
      title: "End of Tenancy / Move In Cleans",
      description: "perfect for landlords & tenants",
      image: "/img/domestic-homepage.webp"
    }
  ];

  const cleaningTasks = [
    "Hoovering", "Mopping", "Dusting", "Ironing", "Bed making", "Internal windows", "Carpets"
  ];

  const reviews = [
    {
      name: "Sarah Mitchell",
      rating: 5,
      text: `Diamond Cleaning has been maintaining our home in ${currentArea} for over a year now. Their attention to detail is exceptional and the staff are always professional. Couldn't be happier!`,
      date: "2 weeks ago"
    },
    {
      name: "James Thompson", 
      rating: 5,
      text: `Absolutely brilliant service! They clean our house fortnightly in ${currentArea} and always do an amazing job. Very reliable and trustworthy team.`,
      date: "1 month ago"
    },
    {
      name: "Emma Wilson",
      rating: 5,
      text: `Used Diamond Cleaning for our end of tenancy clean in ${currentArea}. Got our full deposit back thanks to their thorough work. Highly recommend!`,
      date: "3 weeks ago"
    },
    {
      name: "Lisa Davis",
      rating: 5,
      text: `Had a deep clean done before moving into our new home in ${currentArea}. The team was fantastic and left everything spotless. Great value for money!`,
      date: "2 months ago"
    },
    {
      name: "Michael Brown",
      rating: 5,
      text: `Regular weekly cleaning service is excellent in ${currentArea}. They're always punctual, professional and our house always looks amazing after they visit.`,
      date: "1 week ago"
    },
    {
      name: "Rachel Green",
      rating: 5,
      text: `One-off deep clean service was incredible in ${currentArea}. They cleaned places I didn't even know needed cleaning! Will definitely book again.`,
      date: "3 weeks ago"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header currentArea={currentArea} />
      
      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('/img/domestic-2.webp')`
          }}
        />
        
        <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Domestic Cleaning Services in {currentArea}
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl mx-auto">
              Professional home cleaning services you can trust in {currentArea}. From regular weekly cleans to deep cleaning and end-of-tenancy services.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/book"
                className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
                onClick={handleLinkClick}
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                <span>Book Online Now</span>
              </Link>
              <a
                href="tel:07878522378"
                className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>Call: 07878 522378</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Domestic Cleaning Services in {currentArea}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive home cleaning solutions tailored to your needs in {currentArea} and surrounding areas
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div 
                  className="h-48 bg-cover bg-center"
                  style={{ backgroundImage: `url('${service.image}')` }}
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 capitalize">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Types Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Regular Cleaning Card */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="text-center mb-6">
                <SafeIcon icon={FiHome} className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Regular domestic cleaning in {currentArea}
                </h3>
              </div>
              
              <div className="space-y-4 text-gray-700">
                <p>
                  Keep your house in tip top condition with our regular cleaning service in {currentArea}, which can be booked weekly or fortnightly.
                </p>
                <p>
                  Our cleaners are insured and vetted, so you can leave them to it or stay in whilst they do the clean.
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center space-x-2 mb-2">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Weekly or fortnightly service</span>
                </div>
                <div className="flex items-center space-x-2 mb-2">
                  <SafeIcon icon={FiShield} className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Fully insured & vetted cleaners</span>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiClock} className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Flexible scheduling</span>
                </div>
              </div>
            </div>

            {/* One-off Cleaning Card */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="text-center mb-6">
                <SafeIcon icon={FiClock} className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  One-off domestic cleaning in {currentArea}
                </h3>
              </div>
              
              <div className="space-y-4 text-gray-700">
                <p>
                  Need your home deep cleaned in {currentArea} but don't have the time to do it yourself? This service is perfect for you.
                </p>
                <p>
                  One of our cleaners can be booked in for a one-off clean from 2-6 hours, depending on what needs doing.
                </p>
                <p>
                  Our cleaners are vetted and insured so you don't have to be at home when the clean takes place.
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center space-x-2 mb-2">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">2-6 hours service options</span>
                </div>
                <div className="flex items-center space-x-2 mb-2">
                  <SafeIcon icon={FiShield} className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">No need to be home</span>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiHome} className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">Deep cleaning specialists</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-gray-600 mb-6">
              Still have a question about our domestic cleaning services in {currentArea}? View some of our most frequently asked questions
            </p>
            <p className="text-lg text-gray-900 font-semibold mb-6">
              Book a great cleaner today! Call our friendly office team today on 07878 522378 to discuss your cleaning needs in {currentArea}.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            You can depend on us in {currentArea}!
          </h2>
          
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Here are just some of the cleaning tasks we can do for you in {currentArea}:
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-12">
            {cleaningTasks.map((task, index) => (
              <div key={index} className="bg-white/10 rounded-lg p-4 text-center">
                <span className="text-white font-medium">{task}</span>
              </div>
            ))}
          </div>
          
          <div className="bg-white/10 rounded-2xl p-8 mb-12">
            <p className="text-blue-100 text-lg leading-relaxed">
              Don't hesitate to ask about a cleaning task that is not listed, as long as it's reasonable our cleaners in {currentArea} will happily do it for you.
            </p>
          </div>
          
          <div className="space-y-4">
            <p className="text-xl text-white mb-8">
              Let one of our cleaners take care of all the house cleaning tasks on your behalf in {currentArea}. Ready to get started?
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/book"
                className="bg-white text-blue-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
                onClick={handleLinkClick}
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                <span>Enquire Online</span>
              </Link>
              <a
                href="tel:07878522378"
                className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>Call: 07878 522378</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Domestic Reviews Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Domestic Cleaning Reviews in {currentArea}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See what our satisfied domestic cleaning customers in {currentArea} have to say
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <SafeIcon icon={FiUser} className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{review.name}</h4>
                    <p className="text-sm text-gray-600">Domestic Customer - {currentArea}</p>
                  </div>
                </div>
                
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 mr-2">
                    {[...Array(review.rating)].map((_, i) => (
                      <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">{review.date}</span>
                </div>
                
                <p className="text-gray-700 leading-relaxed">"{review.text}"</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href="https://share.google/xr3mZOVXjGPtaFHeP"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              <span>Read More Reviews on Google</span>
            </a>
          </div>
        </div>
      </section>

      {/* Areas Section */}
      <AreasSection currentArea={currentArea} />

      <Footer />
    </div>
  );
};

const formatAreaName = (area) => {
  return area
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default DomesticPage;